import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { CircularProgress } from '@mui/material';

import dayjs from 'shared/helpers/dayjs';
import { Alerts, Arrow } from 'icons/untitledui';
import {
  NoteCaptureSection,
  NoteCaptureProcessing,
} from 'shiftNotes/components/NoteCapture/_index';
import {
  type PendingUpload,
  useShiftNoteCapture,
} from 'shiftNotes/helpers/ShiftNoteCaptureProvider';
import { useUser } from 'shared/hooks/api/user';
import FilterGroupingControl from 'events/components/FilterGroupingControl/FilterGroupingControl';
import {
  EMPTY_FILTERS,
  FilterOptions,
  FilterValues,
  GroupingKey,
} from 'events/components/FilterGroupingControl/_types';
import { usePosthogCapture } from 'events/posthogCapture/_index';
import EventCard, { type EventCardTag } from 'events/components/EventCard';
import EmptyActivity from 'events/components/EmptyActivity';
import EmptyActivityOnboarding from 'events/components/EmptyActivityOnboarding';
import { getUrgencyColors, getUrgencyLabel } from 'dashboard/urgencyColors';
import { EventStatus, Event, EventTimelineNote } from 'events/_types';
import { EventGroup } from 'events/hooks/useEventGroups';
import getEventUrgencyKey from 'events/helpers/getEventUrgencyKey';
import isEventResolved from 'events/helpers/isEventResolved';
import {
  collectVisibleEntityTags,
  formatCategoryLabel,
  getIconForExtractionClass,
} from 'shiftNotes/helpers/entityTagUtils';
import getNoteAuthorName from 'shiftNotes/helpers/getNoteAuthorName';

import * as Styled from 'events/styles/EventsHomePage.style';
import * as Text from 'theme/components/Text.style';

function collectAuthors(notes: EventTimelineNote[], t: TFunction): string[] {
  const seen = new Set<string>();
  const authors: string[] = [];
  const addAuthor = (key: string, name: string) => {
    if (seen.has(key) || !name) return;
    seen.add(key);
    authors.push(name);
  };
  notes.forEach((note) => {
    const cameras = note.cameras ?? [];
    if (!note.user && cameras.length > 0) {
      cameras.forEach((camera) => addAuthor(camera.id, camera.display_name));
      return;
    }
    addAuthor(note.user?.id ?? note.source, getNoteAuthorName(note, t));
  });
  return authors;
}

function collectEntityTags(notes: EventTimelineNote[]): EventCardTag[] {
  const extractions = notes.flatMap((note) => note.entity_extractions ?? []);
  return collectVisibleEntityTags(extractions).map((tag) => ({
    id: tag.key,
    label: formatCategoryLabel(tag.category),
    icon: getIconForExtractionClass(tag.extractionClass),
  }));
}

interface ActivityListProps {
  filters: FilterValues;
  grouping: GroupingKey;
  filterOptions: FilterOptions;
  onFiltersChange: (values: FilterValues) => void;
  onGroupingChange: (grouping: GroupingKey) => void;
  events: Event[];
  eventGroups: EventGroup[];
  isLoading: boolean;
  hasAnyNotes: boolean;
  hasEventsHiddenByFilters: boolean;
  isDesktopView: boolean;
  isViewingToday: boolean;
  isEventSelected: (eventId: string) => boolean;
  onEventSelect: (eventId: string) => void;
  unseenEventIds: ReadonlySet<string>;
}

export default function ActivityList({
  filters,
  grouping,
  filterOptions,
  onFiltersChange,
  onGroupingChange,
  events,
  eventGroups,
  isLoading,
  hasAnyNotes,
  hasEventsHiddenByFilters,
  isDesktopView,
  isViewingToday,
  isEventSelected,
  onEventSelect,
  unseenEventIds,
}: ActivityListProps): JSX.Element {
  const { t } = useTranslation('pageEvents');
  const capture = usePosthogCapture();
  const { pendingUploads, retryUpload, dismissUpload } = useShiftNoteCapture();
  const { data: currentUser } = useUser();
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    () => new Set()
  );

  const existingNoteIds = useMemo(() => {
    const set = new Set<string>();
    events.forEach((event) => {
      (event.shift_notes ?? []).forEach((note) => set.add(note.id));
    });
    return set;
  }, [events]);

  const currentUserNoteTimes = useMemo(() => {
    const currentUserId = currentUser?.id;
    if (!currentUserId) return [] as number[];
    const times: number[] = [];
    events.forEach((event) => {
      (event.shift_notes ?? []).forEach((note) => {
        if (note.user?.id === currentUserId) {
          times.push(new Date(note.created).getTime());
        }
      });
    });
    return times.sort((a, b) => a - b);
  }, [events, currentUser?.id]);

  const pendingItems = useMemo(() => {
    const authorName = currentUser
      ? `${currentUser.first_name} ${currentUser.last_name}`.trim()
      : '';
    const claimedNoteTimes = new Set<number>();
    const MATCH_WINDOW_MS = 60_000;
    const CLOCK_SKEW_MS = 5_000;
    const isConsumedByEvent = (u: PendingUpload): boolean => {
      const match = currentUserNoteTimes.find(
        (noteTime) =>
          !claimedNoteTimes.has(noteTime) &&
          noteTime >= u.createdAt - CLOCK_SKEW_MS &&
          noteTime <= u.createdAt + MATCH_WINDOW_MS
      );
      if (match === undefined) return false;
      claimedNoteTimes.add(match);
      return true;
    };
    return [...pendingUploads]
      .filter((u) => u.status === 'pending')
      .sort((a, b) => a.createdAt - b.createdAt)
      .filter((u) => {
        // Once the created note has appeared in the events data, stop rendering
        // the pending card — the real event card takes its place at the same slot.
        if (u.createdNoteIds && u.createdNoteIds.length > 0) {
          if (u.createdNoteIds.every((id) => existingNoteIds.has(id))) {
            return false;
          }
        }
        return !isConsumedByEvent(u);
      })
      .map((upload) => ({
        kind: 'pending' as const,
        key: `pending-${upload.id}`,
        sortTime: upload.createdAt,
        description:
          upload.data instanceof FormData ? '' : upload.data.text_note || '',
        authors: authorName ? [authorName] : [],
        time: dayjs(upload.createdAt).format('h:mm A'),
      }));
  }, [pendingUploads, currentUser, existingNoteIds, currentUserNoteTimes]);

  const erroredItems = useMemo(
    () =>
      pendingUploads
        .filter((u) => u.status === 'error')
        .map((upload) => ({
          kind: 'error' as const,
          key: `error-${upload.id}`,
          sortTime: upload.createdAt,
          upload,
        })),
    [pendingUploads]
  );

  const hasPendingOrError = pendingItems.length > 0 || erroredItems.length > 0;

  const firstNonPinnedGroupIdx = useMemo(
    () => eventGroups.findIndex((g) => g.key !== 'pinned'),
    [eventGroups]
  );

  useEffect(() => {
    setCollapsedGroups(new Set());
  }, [grouping]);

  const toggleGroup = useCallback((groupKey: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  }, []);

  const handleResetFilters = () => {
    onFiltersChange(EMPTY_FILTERS);
    capture.filterReset({ from: 'empty_state' });
  };

  return (
    <Styled.SidebarSection>
      <Styled.ActivityHeader>
        <Text.P3Bold>{t('eventsHome.activity')}</Text.P3Bold>
        <FilterGroupingControl
          values={filters}
          grouping={grouping}
          options={filterOptions}
          onValuesChange={onFiltersChange}
          onGroupingChange={onGroupingChange}
        />
      </Styled.ActivityHeader>
      {isDesktopView && isViewingToday && (
        <NoteCaptureSection mobileView={false} />
      )}
      {isLoading && !hasPendingOrError && (
        <Styled.LoadingWrapper>
          <CircularProgress />
        </Styled.LoadingWrapper>
      )}
      {!isLoading &&
        events.length === 0 &&
        hasEventsHiddenByFilters &&
        !hasPendingOrError && (
          <EmptyActivity
            isViewingToday={isViewingToday}
            onResetFilters={handleResetFilters}
          />
        )}
      {!isLoading &&
        events.length === 0 &&
        !hasEventsHiddenByFilters &&
        (hasAnyNotes || !isViewingToday) &&
        !hasPendingOrError && <EmptyActivity isViewingToday={isViewingToday} />}
      {!isLoading &&
        events.length === 0 &&
        !hasEventsHiddenByFilters &&
        !hasAnyNotes &&
        isViewingToday &&
        !hasPendingOrError && <EmptyActivityOnboarding />}
      {((!isLoading && events.length > 0) || hasPendingOrError) && (
        <Styled.EventList>
          {eventGroups.map((group, groupIdx) => {
            const isCollapsible = Boolean(group.label);
            const expanded = !collapsedGroups.has(group.key);

            type EventItem = { kind: 'event'; sortTime: number; event: Event };
            type PendingItem = (typeof pendingItems)[number];
            type ErrorItem = (typeof erroredItems)[number];
            type ListItem = EventItem | PendingItem | ErrorItem;

            const eventItems: ListItem[] = group.events.map((event) => ({
              kind: 'event',
              sortTime: new Date(event.modified).getTime(),
              event,
            }));
            const mergedItems: ListItem[] =
              groupIdx === firstNonPinnedGroupIdx
                ? [...pendingItems, ...erroredItems, ...eventItems].sort(
                    (a, b) => b.sortTime - a.sortTime
                  )
                : eventItems;

            return (
              <React.Fragment key={group.key}>
                {isCollapsible && (
                  <Styled.EventGroupHeader
                    type="button"
                    aria-expanded={expanded}
                    onClick={() => toggleGroup(group.key)}
                  >
                    <Text.H6 as="span">{group.label}</Text.H6>
                    <Styled.EventGroupHeaderChevron
                      $expanded={expanded}
                      aria-hidden
                    >
                      <Arrow.ChevronUp />
                    </Styled.EventGroupHeaderChevron>
                  </Styled.EventGroupHeader>
                )}
                {expanded &&
                  mergedItems.map((item) => {
                    if (item.kind === 'pending') {
                      return (
                        <EventCard
                          key={item.key}
                          eventId={item.key}
                          status="open"
                          authors={item.authors}
                          description={item.description}
                          noteCount={0}
                          time={item.time}
                          tags={[]}
                          processing
                        />
                      );
                    }
                    if (item.kind === 'error') {
                      return (
                        <NoteCaptureProcessing
                          key={item.key}
                          inline
                          upload={item.upload}
                          onRetry={retryUpload}
                          onDismiss={dismissUpload}
                        />
                      );
                    }
                    const { event } = item;
                    const status: EventStatus = isEventResolved(event.status)
                      ? (event.status as EventStatus)
                      : 'open';
                    const shiftNotes = event.shift_notes ?? [];
                    const urgencyKey =
                      getEventUrgencyKey(shiftNotes) ?? event.priority;
                    const urgencyColors = getUrgencyColors(urgencyKey);
                    const urgencyLabel = getUrgencyLabel(urgencyKey, t);
                    const authors = collectAuthors(shiftNotes, t);
                    const entityTags = collectEntityTags(shiftNotes);
                    const tags: EventCardTag[] = [
                      ...(urgencyLabel && !isEventResolved(status)
                        ? [
                            {
                              id: 'urgency',
                              label: urgencyLabel,
                              icon: Alerts.AlertCircle,
                              colors: urgencyColors ?? undefined,
                            },
                          ]
                        : []),
                      ...entityTags,
                    ];
                    const description =
                      event.summary || shiftNotes[0]?.edited_text || '';
                    return (
                      <EventCard
                        key={event.id}
                        eventId={event.id}
                        status={status}
                        authors={authors}
                        description={description}
                        noteCount={event.shift_notes_count}
                        time={dayjs(event.modified).format('h:mm A')}
                        tags={tags}
                        selected={isEventSelected(event.id)}
                        isPrivate={event.is_private}
                        hasUnseenUpdate={unseenEventIds.has(event.id)}
                        onClick={() => onEventSelect(event.id)}
                      />
                    );
                  })}
              </React.Fragment>
            );
          })}
          {firstNonPinnedGroupIdx === -1 && (
            <>
              {pendingItems.map((item) => (
                <EventCard
                  key={item.key}
                  eventId={item.key}
                  status="open"
                  authors={item.authors}
                  description={item.description}
                  noteCount={0}
                  time={item.time}
                  tags={[]}
                  processing
                />
              ))}
              {erroredItems.map((item) => (
                <NoteCaptureProcessing
                  key={item.key}
                  inline
                  upload={item.upload}
                  onRetry={retryUpload}
                  onDismiss={dismissUpload}
                />
              ))}
            </>
          )}
        </Styled.EventList>
      )}
    </Styled.SidebarSection>
  );
}
